// packages/core/src/learning/schools/risk-management.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
//
// ID-bound fields (figures / featuredLessonIds / debateIds) are left [] for
// the post-authoring cross-reference pass. The exhibit's worldview block named
// 'nassim-taleb'; that figure id is valid in figureRegistry.ts and should be
// wired into `figures` during the cross-ref pass, not here.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const RISK_MANAGEMENT_EXHIBIT: SchoolExhibitEntry = {
	id: 'risk-management',
	tagline:
		'Risk management believes the downside deserves a seat at the table.',
	worldviewSummary:
		'Risk management is paying attention to what can break before life forces you to notice it. This lens protects you from fragile plans, missing insurance, thin emergency funds, concentrated bets, bad assumptions, and decisions that only work when everything goes right. It accepts a real tradeoff: protection can feel slower than chasing upside. The blind spot is that too much caution can turn into fear, delay, and underbuilding the life you actually want.',
	coreBeliefs: [
		'Risk management believes the first job of money is to keep you from getting wiped out.',
		'Risk management believes a good plan survives stress, not just spreadsheets.',
		'Risk management believes emergency funds, insurance, and diversification are boring for a reason.',
		'Risk management believes concentration can create upside, but it also creates fragility.',
		'Risk management believes confidence should be tested against bad timing, job loss, illness, repairs, and market drops.',
		'Risk management believes freedom grows when one bad event cannot control your whole life.',
	],
	warnsAgainst: [
		'Building plans that only work if income, markets, health, cars, housing, and relationships behave perfectly.',
		'Confusing optimism with preparation.',
		'Ignoring insurance, cash reserves, legal documents, and concentration risk because they feel boring.',
		'Taking bigger risks because nothing bad has happened yet.',
	],
	helps: 'This lens helps adults who want more stability, cleaner backup plans, and fewer life surprises turning into money emergencies. It may not fit when caution becomes avoidance and the user never lets money support growth, movement, or opportunity.',
	figures: [],
	canon: [
		{
			title: 'Margin of safety',
			oneLineIdea:
				'Leave room for being wrong, late, unlucky, or surprised.',
		},
		{
			title: 'Emergency runway',
			oneLineIdea:
				'Cash reserves buy time when income stops, bills spike, or life gets messy.',
		},
		{
			title: 'Diversification',
			oneLineIdea:
				'Do not let one company, one asset, one job, or one bet decide your whole future.',
		},
		{
			title: 'Insurance as downside control',
			oneLineIdea:
				'Insurance is not exciting. It is a way to stop rare events from becoming permanent damage.',
		},
		{
			title: 'Fragility check',
			oneLineIdea:
				'A plan is fragile when one normal bad event can break it.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-risk-management',
	debateIds: [],
	applyActions: [
		{
			label: 'Check how much runway protects you from one bad month',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Look at your current money picture for weak spots',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Test how a surprise expense changes your future options',
			action: {
				type: 'openSimulator',
				label: 'Simulate the hit',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Write the risks your current plan is ignoring',
			action: {
				type: 'noToolAction',
				label: 'List your weak spots',
				prompt: 'Write three events that would stress your current plan, then write what would help you absorb each one.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'A money plan is not strong until it survives a bad month.',
			kind: 'hook',
		},
		{
			line: 'Risk management is boring until boring saves your future.',
			kind: 'hook',
		},
		{
			line: 'What almost broke your finances because you did not plan for it?',
			kind: 'interview-question',
		},
		{
			line: 'What backup plan gave you the most peace once life got messy?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches people to test the downside before real life does it for them.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Financial freedom is not just upside. It is surviving the hit.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how one surprise expense can expose a fragile money plan.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask people what they wish they had protected before the emergency happened.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
